/**
 * الفرص — Opportunity Finder.
 * تغيير رأس المال/المدة/المخاطرة/المدن يعيد ترتيب الفرص عبر Provider (لا حساب هنا).
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { color } from '@/design/tokens';
import { Card, Chip, SectionTitle, SegmentedControl } from '@/components/primitives/Primitives';
import { OpportunityCard } from '@/components/data/Market';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { UnavailablePanel } from '@/components/states/Unavailable';
import { useOpportunities, useWatchlist } from '@/services/market';
import { useToast } from '@/app/AppShell';
import { compactSar } from '@/lib/format';
import type { CityName, HorizonKey, RiskAppetite } from '@/domain/types';

const HORIZONS: { value: HorizonKey; label: string }[] = [
  { value: '1Y', label: 'سنة' },
  { value: '3Y', label: '٣ سنوات' },
  { value: '5Y', label: '٥ سنوات' },
];

const RISKS: { value: RiskAppetite; label: string }[] = [
  { value: 'low', label: 'منخفضة' },
  { value: 'medium', label: 'متوسطة' },
  { value: 'high', label: 'مرتفعة' },
];

const CITIES: CityName[] = ['الرياض', 'جدة'];

export default function OpportunitiesRoute(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const watchlist = useWatchlist();
  const [capital, setCapital] = useState(5_000_000);
  const [horizon, setHorizon] = useState<HorizonKey>('3Y');
  const [risk, setRisk] = useState<RiskAppetite>('medium');
  const [cities, setCities] = useState<CityName[]>(['الرياض']);

  const state = useOpportunities({ capital, horizon, risk, cities });

  function toggleCity(c: CityName): void {
    setCities((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));
  }

  return (
    <div className="nq-enter" style={{ paddingTop: 36, display: 'grid', gap: 18 }}>
      <h1 style={{ fontSize: 28, fontWeight: 600, letterSpacing: '-.02em' }}>الفرص</h1>

      <Card as="section">
        <div style={{ display: 'grid', gap: 20 }}>
          <div>
            <div className="nq-label" style={{ marginBottom: 10 }}>
              رأس المال —{' '}
              <span className="nq-num" dir="ltr">
                {compactSar(capital)}
              </span>
            </div>
            <input
              type="range"
              min={1_000_000}
              max={50_000_000}
              step={500_000}
              value={capital}
              onChange={(e) => setCapital(Number(e.target.value))}
              aria-label="رأس المال"
              style={{ width: '100%', accentColor: color.teal }}
            />
          </div>

          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 240px' }}>
              <div className="nq-label" style={{ marginBottom: 8 }}>مدة الاحتفاظ</div>
              <SegmentedControl options={HORIZONS} value={horizon} onChange={setHorizon} ariaLabel="مدة الاحتفاظ" />
            </div>
            <div style={{ flex: '1 1 240px' }}>
              <div className="nq-label" style={{ marginBottom: 8 }}>المخاطرة</div>
              <SegmentedControl options={RISKS} value={risk} onChange={setRisk} ariaLabel="المخاطرة" />
            </div>
            <div style={{ flex: '1 1 240px' }}>
              <div className="nq-label" style={{ marginBottom: 8 }}>المدن</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {CITIES.map((c) => (
                  <Chip key={c} label={c} selected={cities.includes(c)} onClick={() => toggleCity(c)} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </Card>

      <SectionTitle title="الفرص المرتّبة" />

      {state.status === 'unavailable' ? (
        <UnavailablePanel
          reason="coming-soon"
          title="مؤشر الفرص"
          detail="ترتيب الفرص يحتاج محرّكات لم تُطلق بعد. لن نرتّب أحياءً بأرقام غير حقيقية."
        />
      ) : state.status === 'loading' ? (
        <LoadingState message="نرتّب الفرص حسب معاييرك..." />
      ) : state.status === 'error' ? (
        <ErrorState message="تعذّر تحميل الفرص الآن." onRetry={state.reload} />
      ) : state.status === 'empty' ? (
        <EmptyState
          title="لا توجد فرص مطابقة"
          description="وسّع رأس المال أو أضف مدينة أخرى أو ارفع مستوى المخاطرة المقبول."
          actionLabel="استكشف الخريطة"
          onAction={() => navigate('/map')}
        />
      ) : (
        <div style={{ display: 'grid', gap: 14 }}>
          {(state.data ?? []).map((o) => (
            <OpportunityCard
              key={o.districtId}
              opportunity={o}
              watched={watchlist.isWatched(o.districtId)}
              onOpen={() => navigate(`/district/${encodeURIComponent(o.districtId)}`)}
              onWatch={() => {
                void watchlist.add(o.districtId);
                toast('أضفنا الحي إلى المراقبة');
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
